import React, { useState } from 'react';
import client from '../sanityClient';

const RequestModal = ({ isOpen, onClose, user }) => {
  const [formData, setFormData] = useState({
    designer: '',
    itemDescription: '',
    budget: '',
    imageUrls: [''] // Start with one empty URL field
  });
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const addImageUrlField = () => {
    setFormData(prev => ({
      ...prev,
      imageUrls: [...prev.imageUrls, '']
    }));
  };

  const removeImageUrlField = (index) => {
    setFormData(prev => ({
      ...prev,
      imageUrls: prev.imageUrls.filter((_, i) => i !== index)
    }));
  };

  const handleImageUrlChange = (index, value) => {
    setFormData(prev => ({
      ...prev,
      imageUrls: prev.imageUrls.map((url, i) => i === index ? value : url)
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess(false);
    setIsSubmitting(true);

    try {
      // Filter out empty URLs
      const validUrls = formData.imageUrls.filter(url => url.trim() !== '');

      // Create request in Sanity
      await client.create({
        _type: 'request',
        designer: formData.designer,
        itemDescription: formData.itemDescription,
        budget: parseFloat(formData.budget),
        photos: validUrls,
        requestedBy: {
          email: user.email,
          name: user.name || user.email
        }
      });

      setSuccess(true);
      // Wait 1.5 seconds before closing
      setTimeout(() => {
        onClose();
        setFormData({
          designer: '',
          itemDescription: '',
          budget: '',
          imageUrls: ['']
        });
        setSuccess(false);
      }, 1500);
    } catch (err) {
      console.error('Error submitting request:', err);
      setError('Failed to submit request. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 pointer-events-none">
      {/* Backdrop */}
      <div
        className={`
          fixed inset-0 bg-black bg-opacity-30 transition-opacity duration-300
          ${isOpen ? 'opacity-100 pointer-events-auto' : 'opacity-0 pointer-events-none'}
        `}
        onClick={onClose}
        style={{ zIndex: 10 }}
      />
      {/* Modal */}
      <div
        className={`
          fixed right-0 top-0 h-full w-full max-w-md bg-white shadow-xl p-6 overflow-y-auto
          transform transition-transform duration-300
          ${isOpen ? 'translate-x-0' : 'translate-x-full'}
          pointer-events-auto
        `}
        style={{ zIndex: 20 }}
      >
        <button
          onClick={onClose}
          className="absolute top-4 right-4 text-gray-400 hover:text-gray-600"
        >
          <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>

        <div className="mb-6">
          <h2 className="text-3xl text-gray-900 mb-2">Submit a request</h2>
            <p className="text-sm text-gray-600 mt-1">
                Have a piece you’ve been dreaming about? I’ll do my best to find it for you.
            </p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6">
          <div>
            <label htmlFor="designer" className="block text-sm font-medium text-gray-700 mb-1">
              Designer
            </label>
            <input
              type="text"
              id="designer"
              value={formData.designer}
              onChange={(e) => setFormData(prev => ({ ...prev, designer: e.target.value }))}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-black focus:border-transparent"
              required
            />
          </div>

          <div>
            <label htmlFor="itemDescription" className="block text-sm font-medium text-gray-700 mb-1">
              Item Description
            </label>
            <textarea
              id="itemDescription"
              value={formData.itemDescription}
              onChange={(e) => setFormData(prev => ({ ...prev, itemDescription: e.target.value }))}
              rows={4}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-black focus:border-transparent"
              required
            />
          </div>

          <div>
            <label htmlFor="budget" className="block text-sm font-medium text-gray-700 mb-1">
              Budget (USD)
            </label>
            <input
              type="number"
              id="budget"
              value={formData.budget}
              onChange={(e) => setFormData(prev => ({ ...prev, budget: e.target.value }))}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-black focus:border-transparent"
              min="0"
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Reference Image URLs (optional)</label>
            {formData.imageUrls.map((url, index) => (
              <div key={index} className="flex gap-2 mb-2">
                <input
                  type="url"
                  value={url}
                  onChange={(e) => handleImageUrlChange(index, e.target.value)}
                  placeholder="https://example.com/image.jpg"
                  className="flex-1 rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
                />
                <button
                  type="button"
                  onClick={() => removeImageUrlField(index)}
                  className="px-2 py-1 text-red-600 hover:text-red-800"
                >
                  ×
                </button>
              </div>
            ))}
            {formData.imageUrls.length < 5 && (
              <button
                type="button"
                onClick={addImageUrlField}
                className="mt-2 text-sm text-indigo-600 hover:text-indigo-800"
              >
                + Add another image URL
              </button>
            )}
          </div>

          {error && (
            <div className="text-red-600 text-sm">{error}</div>
          )}

          {success && (
            <div className="bg-green-50 text-green-800 p-4 rounded-md flex items-center">
              <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
              Request submitted successfully!
            </div>
          )}

          <button
            type="submit"
            className="btn-primary w-full relative"
            disabled={isSubmitting}
          >
            {isSubmitting ? (
              <span className="flex items-center justify-center">
                <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                Submitting...
              </span>
            ) : success ? (
              'Submitted!'
            ) : (
              'Submit Request'
            )}
          </button>
        </form>
      </div>
    </div>
  );
};

export default RequestModal; 